import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

export default function Suppliers() {
  const [items, setItems] = useState([]);
  const [bySupplier, setBySupplier] = useState({ items: [], total_outstanding: 0 });
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", contact_person: "", phone: "", email: "", address: "", gstin: "" });

  const load = () => Promise.all([
    api.get("/suppliers").then(r => setItems(r.data)),
    api.get("/finance/vendor-credits/by-supplier").then(r => setBySupplier(r.data)).catch(() => setBySupplier({ items: [], total_outstanding: 0 })),
  ]);
  useEffect(() => { load(); }, []);

  // Map outstanding by supplier id / name
  const outstandingFor = (s) => {
    const byId = bySupplier.items.find(x => x.supplier_id && x.supplier_id === s.id);
    if (byId) return byId;
    return bySupplier.items.find(x => !x.supplier_id && x.supplier_name === s.name) || { balance: 0, open_credits: 0, oldest_due_date: null };
  };

  const save = async () => {
    if (!f.name) { toast.error("Name required"); return; }
    try {
      await api.post("/suppliers", f);
      toast.success("Saved");
      setOpen(false); setF({ name: "", contact_person: "", phone: "", email: "", address: "", gstin: "" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => { try { await api.delete(`/suppliers/${id}`); load(); } catch { toast.error("Failed"); } };

  return (
    <div className="space-y-5" data-testid="suppliers-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Network</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Suppliers</h1>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="add-supplier-btn"><Plus className="w-4 h-4 mr-1" />New supplier</Button>
      </div>

      {/* Payables summary */}
      <Card className={`p-5 ${bySupplier.total_outstanding > 0 ? "bg-rose-50 border-rose-200" : "bg-emerald-50 border-emerald-200"} border`}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Total payables (we owe suppliers)</div>
            <div className="font-heading text-3xl font-bold mt-1" data-testid="suppliers-total-payable">
              ₹{(bySupplier.total_outstanding || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
            </div>
          </div>
          <div className="text-xs text-muted-foreground">
            Across {bySupplier.items.length} suppliers · Settle via <span className="font-semibold">Financing → Vendor Credits</span>
          </div>
        </div>
      </Card>

      <Card className="p-4">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Contact</TableHead>
            <TableHead>Phone</TableHead>
            <TableHead>GSTIN</TableHead>
            <TableHead className="text-right">We owe</TableHead>
            <TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map(s => {
              const o = outstandingFor(s);
              return (
                <TableRow key={s.id} data-testid={`supplier-row-${s.id}`}>
                  <TableCell className="font-medium">{s.name}</TableCell>
                  <TableCell className="text-sm">{s.contact_person || "—"}</TableCell>
                  <TableCell className="font-mono text-sm">{s.phone || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{s.gstin || "—"}</TableCell>
                  <TableCell className="text-right">
                    {o.balance > 0 ? (
                      <span className="font-mono font-semibold text-rose-700" data-testid={`supplier-owed-${s.id}`}>
                        ₹{o.balance.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                        <span className="block text-[10px] text-rose-500 font-normal">{o.open_credits} open · oldest {o.oldest_due_date || "—"}</span>
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right"><Button variant="ghost" size="sm" onClick={() => del(s.id)} data-testid={`supplier-del-${s.id}`}><Trash2 className="w-4 h-4" /></Button></TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>New supplier</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div><Label>Name *</Label><Input value={f.name} onChange={e => setF(p => ({ ...p, name: e.target.value }))} data-testid="sup-name" /></div>
            <div><Label>Contact person</Label><Input value={f.contact_person} onChange={e => setF(p => ({ ...p, contact_person: e.target.value }))} /></div>
            <div><Label>Phone</Label><Input value={f.phone} onChange={e => setF(p => ({ ...p, phone: e.target.value }))} /></div>
            <div><Label>Email</Label><Input value={f.email} onChange={e => setF(p => ({ ...p, email: e.target.value }))} /></div>
            <div><Label>GSTIN</Label><Input value={f.gstin} onChange={e => setF(p => ({ ...p, gstin: e.target.value }))} /></div>
            <div><Label>Address</Label><Input value={f.address} onChange={e => setF(p => ({ ...p, address: e.target.value }))} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="save-supplier">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
